/* ══════════════════════════════════════════════════════════════════════════
   edgeos-chrome.js — shared Edge desk craft (v390 · CMC single-surface)

   Aligns Edge interiors with Ops Field Glass liquid language:
   instrument meters · glass plates · honest empties · quiet maturity.
   Soft≠Live: never invent Live KPIs; “—” / pending only when truly empty.
   ══════════════════════════════════════════════════════════════════════════ */
import { html, Ico, Alert } from "./ui.js";

/** Posture meter chip · same density as Ops / Edge shell KPIs. */
export function EdgeMeters({ items = [], ariaLabel = "Edge meters" }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return html`<div className="edge-plate__meters" role="group" aria-label=${ariaLabel}>
    ${list.map((it) => {
      const on =
        it.on === true || it.on === "1"
          ? "1"
          : it.on === false || it.on === "0"
            ? "0"
            : it.on != null
              ? String(it.on)
              : "1";
      return html`<div key=${it.k || it.label} className="edge-plate__m" data-on=${on}>
        <span>${it.k || it.label}</span>
        <b>${it.v != null ? it.v : "—"}</b>
        ${it.em ? html`<em>${it.em}</em>` : null}
      </div>`;
    })}
  </div>`;
}

/** Instrument plate — replaces dense generic Card chrome inside Edge. */
export function EdgePlate({
  title,
  meta,
  sub,
  acts,
  children,
  cls = "",
  tone = "",
}) {
  return html`<section
    className=${`edge-plate ${cls}`.trim()}
    data-tone=${tone || undefined}>
    <header className="edge-plate__hd">
      <div className="edge-plate__hd-copy">
        ${meta ? html`<p className="edge-plate__meta">${meta}</p>` : null}
        ${title ? html`<h3 className="edge-plate__title">${title}</h3>` : null}
        ${sub ? html`<p className="edge-plate__sub">${sub}</p>` : null}
      </div>
      ${acts ? html`<div className="edge-plate__hd-acts">${acts}</div>` : null}
    </header>
    <div className="edge-plate__body">${children}</div>
  </section>`;
}

/** Compact Soft≠Live banner — not a blue marketing alert. */
export function EdgeNote({ tone = "info", title, say }) {
  const t = tone === "warn" || tone === "bad" ? tone : "info";
  return html`<div className="edge-note" data-tone=${t} role="status">
    <span className="edge-note__mark" aria-hidden="true"/>
    <div className="edge-note__copy">
      ${title ? html`<b>${title}</b>` : null}
      ${say ? html`<p>${say}</p>` : null}
    </div>
  </div>`;
}

export function EdgeEmpty({ title, say, acts }) {
  return html`<div className="edge-empty">
    <div className="edge-empty__mark" aria-hidden="true">
      <${Ico} d="chip" w=${18}/>
    </div>
    <b>${title || "Nothing here yet"}</b>
    ${say ? html`<p>${say}</p>` : null}
    ${acts ? html`<div className="edge-empty__acts">${acts}</div>` : null}
  </div>`;
}

export function EdgeActRow({ children, cls = "" }) {
  return html`<div className=${`edge-act edge-os__acts ${cls}`.trim()}>${children}</div>`;
}

/** Honest ledger table shell — no invent rows. */
export function EdgeTable({ cols, note, rows }) {
  const list = Array.isArray(cols) ? cols : [];
  const n = Math.max(list.length, 1);
  const body = Array.isArray(rows) ? rows : [];
  return html`<div className="edge-table" role="table" style=${{ "--edge-cols": String(n) }}>
    <div className="edge-table__tr edge-table__tr--h" role="row">
      ${list.map((c) => html`<span key=${c} role="columnheader" className="edge-table__cell">${c}</span>`)}
    </div>
    ${body.length
      ? body.map(
          (r, i) => html`<div key=${r.id || i} className="edge-table__tr" role="row">
            ${(r.cells || []).map((c, j) => html`<span key=${j} role="cell" className="edge-table__cell">${c}</span>`)}
          </div>`,
        )
      : html`<div className="edge-table__empty" role="row">
          <p>${note || "No Live rows · surface reserved for bus"}</p>
        </div>`}
  </div>`;
}

/** Key–value instrument lines. */
export function EdgeKV({ rows }) {
  const list = Array.isArray(rows) ? rows : [];
  return html`<dl className="edge-kv">
    ${list.map(
      (r) => html`<div key=${r.k} className="edge-kv__row" data-tone=${r.tone || undefined}>
        <dt>${r.k}</dt>
        <dd>${r.v != null && r.v !== "" ? r.v : "—"}</dd>
      </div>`,
    )}
  </dl>`;
}

/** Device roster pick — Live GET /devices honesty. */
export function EdgeDevicePick({ devices, deviceId, setDeviceId, loading, error }) {
  const list = Array.isArray(devices) ? devices : [];
  return html`
    <${EdgePlate} title="Node" meta="Assigned nodes" sub="Pick one node for this desk">
      ${error
        ? html`<${Alert} tone="warn" title="Device register refused" say=${error}/>`
        : null}
      ${loading && !list.length
        ? html`<${EdgeEmpty} title="Reading Live devices…" say="Register is loading from Live."/>`
        : !list.length
          ? html`<${EdgeEmpty}
              title="No devices on this account"
              say="When Live nodes report in, they appear here. Empty list means no nodes yet — nothing invented."
            />`
          : html`<div className="edge-pick" role="listbox" aria-label="Devices">
              ${list.slice(0, 24).map(
                (d) => html`<button
                  key=${d.id}
                  type="button"
                  role="option"
                  className="edge-pick__item"
                  aria-selected=${deviceId === d.id ? "true" : "false"}
                  data-on=${deviceId === d.id ? "1" : "0"}
                  onClick=${() => setDeviceId(d.id)}>
                  <span className="edge-pick__st" data-on=${d.online ? "1" : "0"}/>
                  <span className="edge-pick__main">
                    <b>${d.label || d.id}</b>
                    <i>${d.id}${d.zone ? " · " + d.zone : ""}${d.net ? " · " + d.net : ""}</i>
                  </span>
                  <em className="edge-pick__tag" data-tone=${d.online ? "ok" : "off"}>
                    ${d.online ? "online" : "quiet"}
                  </em>
                </button>`,
              )}
            </div>`}
    <//>`;
}

export function EdgeMaturity({ maturity, wave }) {
  const m = maturity || "designed";
  const label = m === "live" ? "Live" : m === "deferred" ? "Hold" : "Plan";
  return html`<span className="edge-wave__mat edge-plate__mat" data-mat=${m} title=${wave || ""}>
    <i>${label}</i><b>${m === "live" ? "Live bus" : m === "deferred" ? "Owner pending" : "Plan · no invent"}</b>
  </span>`;
}

/** Nested desk head kept for a11y / off-shell; shell hides via CSS. */
export function EdgeDeskHead({ title, wave, maturity, lede }) {
  return html`<header className="edge-wave__hd" data-nested="1">
    <div>
      <p className="edge-wave__k">Edge</p>
      <h2 className="edge-wave__title">${title}</h2>
      ${lede ? html`<p className="edge-wave__lede">${lede}</p>` : null}
    </div>
    <${EdgeMaturity} wave=${wave} maturity=${maturity}/>
  </header>`;
}

export function EdgeLadder({ steps }) {
  const list = Array.isArray(steps) ? steps : [];
  return html`<ol className="edge-ladder">
    ${list.map(
      (s, i) => html`<li key=${s.id || i} data-on=${s.on ? "1" : "0"}>
        <b>${s.k || `L${i + 1}`}</b>
        <span>${s.v}</span>
      </li>`,
    )}
  </ol>`;
}

export function EdgeTiles({ items }) {
  const list = Array.isArray(items) ? items : [];
  return html`<div className="edge-tiles">
    ${list.map(
      (it) => html`<div key=${it.k || it.title} className="edge-tiles__cell">
        <b>${it.k || it.title}</b>
        <em>${it.v || it.em || ""}</em>
      </div>`,
    )}
  </div>`;
}
